import React, { useMemo } from 'react';
import { ContentLexicalStateV1 } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  AddPostCommentReplyDocument,
  CollectorCommentsDocument,
  CommentAuthorFragmentDoc,
  CommentRepliesDocument,
  DeletePostCommentReplyDocument,
  getFragment,
  gql,
  PostCommentsDocument,
  PostCommentsRowFragmentDoc,
  ReleasePageDocument,
  SortOrder,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { UserReplyItem } from './UserReplyItem';
import { commentReplyFilterQueryKey } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CommentReplies(
    $pagination: PostCommentCursorConnectionArgs!
    $parentCommentId: UUID!
    $postId: UUID!
  ) {
    commentReplies(pagination: $pagination, parentCommentId: $parentCommentId, postId: $postId) {
      edges {
        node {
          id
          ...PostCommentsRow
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const PAGE_SIZE = 3;

RefetchOnComplete({
  refetch: [
    CommentRepliesDocument,
    CollectorCommentsDocument,
    PostCommentsDocument,
    ReleasePageDocument,
  ],
  trigger: [AddPostCommentReplyDocument, DeletePostCommentReplyDocument],
});

export function UserCommentReplies({
  repliesCount,
  parentCommentId,
  postId,
}: {
  repliesCount: number;
  parentCommentId: string;
  postId: string;
}) {
  const [unfolded, setUnfolded] = React.useState(false);

  const { orderedList, loadMoreNextPage, isLoadingNewPage, hasNextPage, remove } = useInfiniteQuery(
    CommentRepliesDocument,
    {
      enabled: unfolded,
      staleTime: Infinity,
      filterQueryKey: commentReplyFilterQueryKey(parentCommentId),
      getNextPageParam({ data }) {
        return (
          data.commentReplies.pageInfo.hasNextPage && {
            after: data.commentReplies.pageInfo.endCursor,
          }
        );
      },
      variables({ pageParam }) {
        return {
          pagination: {
            after: pageParam?.after,
            first: PAGE_SIZE,
            sort: {
              createdAt: SortOrder.Desc,
            },
          },
          parentCommentId,
          postId,
        };
      },
      list({ commentReplies }) {
        return commentReplies.edges.map(edge => edge.node);
      },
      uniq({ id }) {
        return id;
      },
    },
  );

  const repliesLeft = Math.max(repliesCount - orderedList.length, 0);

  const loadMoreReplies = useStableCallback(async () => {
    if (unfolded) {
      loadMoreNextPage();
      return;
    }

    setUnfolded(true);

    if (orderedList.length) {
      loadMoreNextPage();
    }
  });

  const clearReplies = useStableCallback(async () => {
    remove();
    setUnfolded(false);
  });

  const replies = useMemo(() => {
    return orderedList.map(item => {
      const postComment = getFragment(PostCommentsRowFragmentDoc, item);
      const postAuthor = getFragment(CommentAuthorFragmentDoc, postComment.author);

      const safeParseResult = ContentLexicalStateV1.safeParse(postComment.lexicalContent);
      const lexicalContent = safeParseResult.success ? safeParseResult.data : null;

      return (
        <UserReplyItem
          key={postComment.id}
          replyId={postComment.id}
          parentCommentId={parentCommentId}
          userFragment={postComment.author}
          createdAt={postComment.createdAt}
          message={lexicalContent || postComment.content}
          showArtistTag={postComment.labels.some(label => label === 'RELEASE_ARTIST')}
          showFeedCuratorBadge={false}
          showVerifiedBadge={!!postAuthor.artist}
          showCollectorTag={postComment.currentHolderOfRelease}
          upvoteCount={postComment.upvoteCount}
        />
      );
    });
  }, [orderedList, parentCommentId]);

  return (
    <div className="w-full pl-[47px]">
      {replies}

      {isLoadingNewPage && (
        <div className="flex flex-col gap-4 py-[10px]">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex animate-pulse flex-col gap-1">
              <div className="flex gap-2">
                <div className="h-4 w-4 rounded-full bg-base300" />
                <div className="h-4 w-20 rounded-lg bg-base300" />
                <div className="h-4 w-40 rounded-lg bg-base300" />
              </div>
              <div className="h-4 w-40 rounded-lg bg-base300" />
            </div>
          ))}
        </div>
      )}

      {!!repliesLeft && (
        <button
          className="-mx-1 mb-8 cursor-pointer rounded-md px-2 py-1 hover:bg-neutral100 active:scale-95"
          onClick={loadMoreReplies}
          disabled={isLoadingNewPage}
        >
          <div className="flex items-center gap-2">
            <div className="h-[1px] w-6 bg-base500" />
            <span className="font-base !text-base-m font-medium text-neutral500">
              See {repliesLeft} {repliesLeft === 1 ? 'reply' : 'replies'}
            </span>
          </div>
        </button>
      )}

      {orderedList.length !== 0 && !hasNextPage && !repliesLeft && (
        <button
          className="-mx-1 mb-8 cursor-pointer rounded-md px-2 py-1 hover:bg-neutral100 active:scale-95"
          onClick={clearReplies}
        >
          <div className="flex items-center gap-2">
            <div className="h-[1px] w-6 bg-base500" />
            <span className="font-base !text-base-m font-medium text-neutral500">Hide replies</span>
          </div>
        </button>
      )}
    </div>
  );
}
